<template>
  <v-container fluid
               class="PresentationInsights">

    <v-breadcrumbs :items="breadcrumbs"
                   class="px-0 py-2">
    </v-breadcrumbs>

    <div>
      <h1 class="text--title">
        <span>{{ presentation.name }}</span>
        <v-btn icon
               rounded
               :disabled="form.busy"
               @click.prevent="fetchData">
          <v-icon v-if="form.busy">
            fal fa-fw fa-spin fa-spinner
          </v-icon>
          <v-icon v-else>
            fal fa-fw fa-redo
          </v-icon>
        </v-btn>
      </h1>
    </div>

    <v-card class="mb-4">
      <v-card-title class="pb-0">
        Performance
      </v-card-title>
      <div class="pa-4">

        <div class="d-lg-flex">
          <div class="mb-8 mb-lg-0">
            <div class="mb-1">
              <div style="font-size: 16px;">
                Key Metrics
              </div>
            </div>
            <div class="d-flex font-weight-light">

              <div class="mr-8 text-center">
                <div style="font-size: 40px;">
                  {{ averagePercentViewed }}
                  <sup style="font-size: 16px; margin-left: -10px; top: -.5em;">%</sup>
                </div>
                <div>
                  Avg Viewed
                </div>
              </div>

              <div class="mr-8 text-center">
                <div style="font-size: 40px;">
                  {{ sessions.length }}
                </div>
                <div>
                  {{ sessions.length | pluralize('Visit', false) }}
                </div>
              </div>

              <div class="text-center">
                <div style="font-size: 40px;">
                  {{ uniqueVisitorCount }}
                </div>
                <div>
                  Unique {{ uniqueVisitorCount | pluralize('Visitor', false) }}
                </div>
              </div>

            </div>
          </div>

          <v-spacer class="hidden visible-lg"></v-spacer>

          <div v-if="sessions.length > 0">
            <div class="mb-1">
              <div style="font-size: 16px;">
                Top Pages
                <span style="font-size: 12px;">(by average time per page)</span>
              </div>
            </div>
            <div class="d-flex font-weight-light">

              <div class="d-flex mr-4 mr-lg-12"
                   v-for="page in topPages"
                   :key="page.page_number">

                <v-img :src="page.url"
                       contain
                       width="100"
                       class="mr-1">
                </v-img>
                <div style="font-size: 12px;">
                  <div style="font-size: 14px;">
                    {{ page.avg_seconds | duration }}
                  </div>
                  <div>
                    Page: {{ page.page_number }}
                  </div>
                  <div>
                    Visits: {{ page.num_sessions }}
                  </div>
                </div>

              </div>

            </div>
          </div>

        </div>

      </div>
    </v-card>

    <v-card class="mb-4">
      <v-card-title class="pb-0">
        All Visits
      </v-card-title>
      <div class="pa-4">
        <sessions-table :sessions="sessions"
                        :items-per-page="10"
                        :loading="sessionsBusy">
        </sessions-table>
      </div>
    </v-card>
  </v-container>
</template>
<script>
import {mapGetters} from 'vuex';
import {Form} from 'vform';
import {orderBy, sumBy, uniqBy} from 'lodash';
import analyticsService from '../../../../ml/services/analyticsService';
import PresentationPages from './components/PresentationPages';
import SessionsTable from './components/SessionsTable';
import PercentViewedDonut from './components/PercentViewedDonut';

export default {
  components: {PercentViewedDonut, SessionsTable, PresentationPages},
  props: {
    presentation: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      tab: 0,
      exporting: false, // MLD-1869
      form: new Form({
        presentation_id: this.presentation.id,
        shared_link_id: null,
        //contact_type: 'all',
        //contact_uuid: this.$route.query.contact_uuid ? this.$route.query.contact_uuid : '',
        user_id: null,
        order_by: 'created_at',
        order_dir: 'desc',
      }),
      pages: [],
      sessionsBusy: false,
      sessions: [],
    }
  },
  computed: {
    ...mapGetters([
      'team',
      'user',
    ]),
    uniqueVisitorCount() {
      return uniqBy(this.sessions, 'user_contact_id').length;
    },
    topPages() {
      return orderBy(this.pages.filter(p => p.avg_seconds > 0), ['avg_seconds', 'num_sessions'], ['desc', 'desc']).slice(0, 3);
    },
    averagePercentViewed() {
      if (this.sessions.length === 1) {
        return this.sessions[0].percent_viewed;
      }
      const totalPercentViewed = sumBy(this.sessions, 'percent_viewed');
      if (totalPercentViewed) {
        //return Math.floor(this.sessions.length / totalPercentViewed * 100);
        return Math.floor(totalPercentViewed / this.sessions.length);
      }
      return 0;
    },
    breadcrumbs() {
      return [
        {
          text: 'Content Library',
          disabled: false,
          exact: true,
          to: {name: `${this.$root.routePrefix}.home`},
        },
        {
          text: this.presentation.name,
          disabled: true,
        },
        {
          text: 'Activity',
          disabled: true,
        },
      ]
    },
    presentationID() {
      return parseInt(this.$route.params.presentationID);
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.form.startProcessing();
      await this.fetchSessions()
      return Promise.all([
        this.fetchPages(),
        this.fetchLinks(),
      ])
        .finally(() => {
          this.form.busy = false;
        })
    },
    fetchSessions() {
      if (this.sessionsBusy) {
        return;
      }
      const form = this.form.data();
      form.order_by = 'created_at'
      form.order_dir = 'desc';
      form.user_id = this.user.id;
      if (this.$route.query.shared_link_id) { // MLD-7625
        form.shared_link_id = this.$route.query.shared_link_id;
      }
      this.sessionsBusy = true;
      return analyticsService.getSessions(form)
        .then(data => {
          this.sessions = data;
        })
        .catch(err => {
          this.sessions = [];
        })
        .finally(() => {
          this.sessionsBusy = false;
        })
    },
    fetchLinks() { // MLD-2596
      const form = this.form.data();
      this.links = [];
      return analyticsService.getLinksReport(this.presentation.id, form)
        .then(data => {
          this.links = data;
        })
        .catch(() => {
        })
    },
    fetchPages() {
      this.pages = [];
      const pagesForm = this.form.data();
      return analyticsService.getPagesReport(this.presentation.id, pagesForm)
        .then(data => {
          this.pages = data;
          this.pages.forEach(p => {
            p.avg_seconds = Math.floor(p.avg_seconds);
          })
        })
        .catch(() => {
        })
    },
  }
}
</script>
